import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./css/Analyses.css";
import API_BASE from "./config";
import { usePermissions, RequirePermission } from "./hooks/usePermissions";

const Analyses = () => {
    const [jobs, setJobs] = useState([]); 
    const [expandedLogs, setExpandedLogs] = useState({}); 
    const [logs, setLogs] = useState({}); 
    const [expandedResults, setExpandedResults] = useState({}); 
    const [resultsTree, setResultsTree] = useState({}); 
    const [selectedFiles, setSelectedFiles] = useState({}); 
    const [expandedReports, setExpandedReports] = useState({}); 
    const [reportsHtml, setReportsHtml] = useState({}); 
    const [reportHtml, setReportHtml] = useState(""); 
    const [expandedFolders, setExpandedFolders] = useState({}); 
    const [cognitoUserId, setCognitoUserId] = useState(null); 
    const navigate = useNavigate();

    useEffect(() => { 
      const fetchSession = async () => { 
        try { 
          const response = await fetch(`${API_BASE}/auth/session`, { 
            method: "GET", credentials: "include", 
          }); 
          const data = await response.json(); 
          if (!data.authenticated) { 
            console.error("User not authenticated"); 
            return; 
          } 
          
          setCognitoUserId(data.cognitoUserId); // this is MySQL users.id 
        } catch (err) { 
          console.error("Error fetching session:", err); 
        } 
      }; 
      fetchSession(); 
    }, []);

    useEffect(() => {
      if (!cognitoUserId) return; 
      
      const fetchJobs = async () => {
      try {
          const response = await axios.get(`${API_BASE}/get-analyses`, {
            params: { user_id: cognitoUserId }
          });
          setJobs(response.data);
      } catch (error) {
          console.error("Error fetching analyses:", error);
      }
      };
      fetchJobs();
    }, [cognitoUserId]);

    const loadReport = async (workflowId) => {
      const res = await fetch(`${API_BASE}/tmp_reports/report_${workflowId}.html`);
      const html = await res.text();
      setReportHtml(html);
    };


    const buildTree = (files) => {
      const root = { name: "root", type: "folder", children: {} };

      files.forEach(file => {
        if (!file.path) {
          console.warn("Skipping file with missing path:", file);
          return;
        }

        const relativePath = file.path.replace(/^.*?output\//, "");
        const parts = relativePath.split("/").filter(Boolean);
        let current = root;

        parts.forEach((part, idx) => {
          if (!current.children[part]) {
            current.children[part] = {
              name: part,
              type: idx === parts.length - 1 ? "file" : "folder",
              children: {},
              url: file.url,
              path: file.path
            };
          }
          current = current.children[part];
        });
      });

      return root.children;
    };

    const toggleLogPanel = jobId => {
      const isExpanded = expandedLogs[jobId];
      setExpandedLogs(prev => ({ ...prev, [jobId]: !isExpanded }));

      if (!isExpanded) {
        fetchExecutionLogs(jobId); // fetch logs only when expanding
      }
    };


    const toggleResultsPanel = async job => {
        const jobId = job.id;
        const isExpanded = expandedResults[jobId];

        if (isExpanded) {
        setExpandedResults(prev => ({ ...prev, [jobId]: false }));
        return;
        }

        try {
        let files = [];

        if (job.workflow_type === "generated") {
            const res = await axios.get(`${API_BASE}/get-results`, {
            params: { workflowId: jobId },
            });
            files = res.data.files || [];
        } else if (job.workflow_type === "nfcore") {
            const res = await axios.get(`${API_BASE}/get-nfcore-results`, {
            params: { workflowId: jobId },
            });
            files = (res.data.files || []).filter(f => !f.path.includes("pipeline_info/"));
        }

        const tree = buildTree(files);
        setResultsTree(prev => ({ ...prev, [jobId]: tree }));
        setSelectedFiles(prev => ({ ...prev, [jobId]: {} }));
        setExpandedResults(prev => ({ ...prev, [jobId]: true }));
        } catch (error) {
        console.error(`Error fetching results for job ${jobId}:`, error);
        setResultsTree(prev => ({ ...prev, [jobId]: {} }));
        setExpandedResults(prev => ({ ...prev, [jobId]: true }));
        }
    };

    const toggleReportPanel = async job => {
      const jobId = job.id;
      const isExpanded = expandedReports[jobId];

      if (isExpanded) {
        setExpandedReports(prev => ({ ...prev, [jobId]: false }));
        return;
      }

      try {
        const res = await axios.get(`${API_BASE}/get-execution-report`, {
          params: { workflowId: jobId },
        });
        const reportPath = res.data.path;
        setReportsHtml(prev => ({ ...prev, [jobId]: reportPath }));
        setExpandedReports(prev => ({ ...prev, [jobId]: true }));
      } catch (error) {
        console.error(`Error fetching execution report for job ${jobId}:`, error);
        setReportsHtml(prev => ({ ...prev, [jobId]: null }));
        setExpandedReports(prev => ({ ...prev, [jobId]: true }));
      }
    };

    const fetchExecutionLogs = async (jobId) => {
      try {
        const response = await axios.get(`${API_BASE}/getExecutionLogs`, {
          params: { workflowId: jobId }
        });
        setLogs(prev => ({ ...prev, [jobId]: response.data.logs || [] }));
      } catch (error) {
        console.error(`Error fetching logs for job ${jobId}:`, error);
        setLogs(prev => ({ ...prev, [jobId]: [] }));
      }
    };

    const renderTree = (node, jobId, path = "") => {
        return Object.values(node).map((item, idx) => {
            const fullPath = `${path}/${item.name}`;
            const isChecked = selectedFiles[jobId]?.[fullPath] || false;
            const isExpanded = expandedFolders[jobId]?.[fullPath] || false;

            const toggleCheck = () => {
            setSelectedFiles(prev => ({
                ...prev,
                [jobId]: {
                ...prev[jobId],
                [fullPath]: !isChecked
                }
            }));
            };

            const toggleExpand = () => {
            setExpandedFolders(prev => ({
                ...prev,
                [jobId]: {
                ...prev[jobId],
                [fullPath]: !isExpanded
                }
            }));
            };

            return (
            <div key={idx} style={{ marginLeft: "20px" }}>
                {item.type === "folder" ? (
                <div style={{ cursor: "pointer", fontWeight: "bold" }} onClick={toggleExpand}>
                    📁 {item.name}
                </div>
                ) : (
                <label>
                    <input type="checkbox" checked={isChecked} onChange={toggleCheck} />
                    {item.name}
                </label>
                )}
                {item.type === "folder" && isExpanded && renderTree(item.children, jobId, fullPath)}
            </div>
            );
        });
    };

    const downloadSelectedFiles = async jobId => {
        const selections = selectedFiles[jobId] || {};
        const selectedPaths = Object.entries(selections)
            .filter(([_, checked]) => checked)
            .map(([path]) => path.replace(/^\/+/, ""));

        if (!selectedPaths.length) return;

        try {
            const response = await axios.post(`${API_BASE}/download-selected-files`, {
            workflowId: jobId,
            selectedPaths,
            }, {
            responseType: "blob",
            });

            const blob = new Blob([response.data], { type: "application/zip" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `results_${jobId}.zip`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Download failed:", err);
            alert("Failed to download selected files.");
        }
    };

    return (
    <div className="analyses-container">
      <h2>Submitted Analyses</h2>
      <h4 style={{textAlign:"center"}}>Please note that there may be a delay in updating the status of submitted analyses.</h4>
      <table className="job-summary-table">
        <thead>
          <tr>
            <th>Job Name</th>
            <th>Created On</th>
            <th>Current Status</th>
            <th style={{ width: "50%" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map(job => (
            <React.Fragment key={job.id}>
              <tr>
                <td>{job.short_name}</td>
                <td>{new Date(job.created_at).toLocaleString()}</td>
                <td>{job.status}</td>
                <td>
                  <button className="view-details-button" onClick={() => navigate("/analysisView", { state: { workflowId: job.id, isByop: !!job.is_byop } })}>
                    View Executions
                  </button>
                  <button className="log-toggle-button" onClick={() => toggleLogPanel(job.id)}>
                    {expandedLogs[job.id] ? "Hide Execution Log" : "View Execution Log"}
                  </button>
                </td>
              </tr>

              {expandedLogs[job.id] && (
                <tr>
                  <td colSpan="4">
                    <div className="execution-log-panel">
                      <div className="log-header"><strong>Live Execution Logs</strong></div>
                      <div className="log-output">
                        {logs[job.id]?.length ? (
                          logs[job.id].map((log, index) => (
                            <div key={index} className={`log-line ${log.log_type}`}>
                              {[log.log_time]} {log.log}
                            </div>
                          ))
                        ) : (
                          <div className="log-line">Fetching logs...</div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              )}

            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Analyses;